import { createTheme, type PaletteMode, type Theme } from '@mui/material';
import { lightPalette, darkPalette } from './palettes';

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const buildShadows = (isDark: boolean): Theme['shadows'] => {
  const base: string[] = isDark
    ? [
        'none',
        '0 1px 2px rgba(0,0,0,.5)',
        '0 4px 14px rgba(0,0,0,.5)',
        '0 10px 28px rgba(0,0,0,.5)',
        '0 18px 44px rgba(0,0,0,.55)',
        '0 28px 70px rgba(0,0,0,.6)',
      ]
    : [
        'none',
        '0 1px 2px rgba(15,23,42,.05)',
        '0 4px 14px rgba(15,23,42,.06)',
        '0 10px 28px rgba(15,23,42,.08)',
        '0 18px 44px rgba(15,23,42,.10)',
        '0 28px 70px rgba(15,23,42,.14)',
      ];
  while (base.length < 25) base.push('none');
  return base as Theme['shadows'];
};

export function buildTheme(mode: PaletteMode): Theme {
  const isDark = mode === 'dark';
  const basePalette = isDark ? darkPalette : lightPalette;

  const hairBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)';
  const hairBorderStrong = isDark ? 'rgba(255,255,255,0.16)' : 'rgba(15,23,42,0.16)';
  const hairBorderSoft = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)';

  const theme = createTheme({
    palette: {
      mode,
      ...basePalette,
    },
    typography: {
      fontFamily: FONT,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: { fontSize: '2.25rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 },
      h2: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.2 },
      h3: { fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-0.01em' },
      h4: { fontSize: '1.2rem', fontWeight: 700, letterSpacing: '-0.005em' },
      h5: { fontSize: '1rem', fontWeight: 600 },
      h6: { fontSize: '0.95rem', fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: '-0.005em' },
      overline: { letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 },
      caption: { fontSize: '0.78rem' },
    },
    shape: { borderRadius: 12 },
    shadows: buildShadows(isDark),
    components: {
      MuiCssBaseline: {
        styleOverrides: () => ({
          '*': { boxSizing: 'border-box' },
          html: { scrollBehavior: 'smooth' },
          body: {
            fontFamily: FONT,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            background:
              'radial-gradient(1200px 600px at 10% -10%, var(--mesh-blob1, transparent) 0%, transparent 60%),' +
              'radial-gradient(900px 500px at 90% 10%, var(--mesh-blob2, transparent) 0%, transparent 60%),' +
              'radial-gradient(700px 500px at 50% 110%, var(--mesh-blob3, transparent) 0%, transparent 60%),' +
              'var(--bg)',
            backgroundAttachment: 'fixed',
            color: 'var(--text)',
            minHeight: '100vh',
          },
          '::selection': {
            background: 'var(--primary)',
            color: 'var(--primary-contrast)',
          },
        }),
      },
      MuiButton: {
        defaultProps: { disableElevation: false },
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: '8px 18px',
            fontWeight: 600,
            transition:
              'transform .15s ease, background .2s ease, box-shadow .25s ease, border-color .25s ease, color .25s ease',
          },
          contained: {
            boxShadow: isDark
              ? '0 8px 24px -10px rgba(139,139,255,.6)'
              : '0 8px 24px -10px rgba(91,91,246,.45)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: isDark
                ? '0 16px 36px -12px rgba(139,139,255,.85)'
                : '0 16px 36px -12px rgba(91,91,246,.55)',
            },
            '&:active': { transform: 'translateY(0)' },
          },
          containedPrimary: {
            background: isDark
              ? 'linear-gradient(135deg, #8b8bff 0%, #5ee7f1 100%)'
              : 'linear-gradient(135deg, #5b5bf6 0%, #22c3d1 100%)',
            color: isDark ? '#0a0b14' : '#ffffff',
            '&:hover': {
              background: isDark
                ? 'linear-gradient(135deg, #9d9dff 0%, #6ef0fa 100%)'
                : 'linear-gradient(135deg, #6a6aff 0%, #2fd1de 100%)',
            },
          },
          outlined: {
            borderColor: hairBorderStrong,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.04)',
              borderColor: isDark ? 'rgba(255,255,255,0.22)' : 'rgba(15,23,42,0.22)',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 12,
            border: `1px solid ${hairBorder}`,
            boxShadow: isDark
              ? '0 1px 2px rgba(0,0,0,.4), 0 8px 24px rgba(0,0,0,.25)'
              : '0 1px 2px rgba(15,23,42,0.05), 0 8px 24px rgba(15,23,42,0.06)',
            transition: 'background-color .25s ease, border-color .25s ease',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundImage: 'none',
            border: `1px solid ${hairBorder}`,
            backgroundColor: isDark ? 'rgba(18,20,31,0.7)' : 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(16px) saturate(140%)',
            WebkitBackdropFilter: 'blur(16px) saturate(140%)',
            boxShadow: isDark ? '0 8px 24px rgba(0,0,0,.35)' : '0 8px 24px rgba(15,23,42,0.08)',
            transition: 'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
            '&:hover': {
              borderColor: hairBorderStrong,
              boxShadow: isDark ? '0 16px 40px rgba(0,0,0,.45)' : '0 16px 40px rgba(15,23,42,0.10)',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: hairBorderSoft,
            fontSize: '0.88rem',
            padding: '14px 16px',
          },
          head: {
            fontWeight: 600,
            color: isDark ? 'rgba(236,238,252,0.7)' : 'rgba(11,16,32,0.65)',
            textTransform: 'none',
            letterSpacing: '0.01em',
            fontSize: '0.78rem',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.025)',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:last-child td, &:last-child th': { border: 0 },
            '&.MuiTableRow-hover:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.03)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontWeight: 600,
            letterSpacing: '0.01em',
            fontSize: '0.78rem',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.08)'}`,
            backdropFilter: 'blur(24px) saturate(160%)',
            WebkitBackdropFilter: 'blur(24px) saturate(160%)',
            backgroundColor: isDark ? 'rgba(20,22,34,0.96)' : 'rgba(255,255,255,0.96)',
            boxShadow: isDark ? '0 40px 80px rgba(0,0,0,0.6)' : '0 40px 80px rgba(15,23,42,0.18)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.15rem',
            fontWeight: 700,
            letterSpacing: '-0.01em',
            padding: '20px 24px',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { padding: '4px 24px 20px' },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: { padding: '12px 24px 20px' },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
              transition: 'background-color .2s ease',
              '& fieldset': {
                borderColor: hairBorder,
                transition: 'border-color .2s ease',
              },
              '&:hover fieldset': {
                borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(15,23,42,0.22)',
              },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: { outlined: { borderRadius: 10 } },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${hairBorder}`,
            backgroundColor: isDark ? 'rgba(18,20,31,0.72)' : 'rgba(255,255,255,0.72)',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: isDark ? 'rgba(18,20,31,0.82)' : 'rgba(255,255,255,0.82)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            borderBottom: `1px solid ${hairBorder}`,
            boxShadow: 'none',
            color: isDark ? '#eceefc' : '#0b1020',
          },
        },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 10 } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 10,
            backgroundColor: isDark ? '#1a1d2b' : '#0b1020',
            fontSize: '0.78rem',
            padding: '6px 10px',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            border: `1px solid ${hairBorder}`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderRadius: 10,
            marginBottom: 2,
            position: 'relative',
            '&.Mui-selected': {
              background: isDark
                ? 'linear-gradient(135deg, rgba(139,139,255,0.18), rgba(94,231,241,0.1))'
                : 'linear-gradient(135deg, rgba(91,91,246,0.14), rgba(34,195,209,0.08))',
              '&:hover': {
                background: isDark
                  ? 'linear-gradient(135deg, rgba(139,139,255,0.22), rgba(94,231,241,0.13))'
                  : 'linear-gradient(135deg, rgba(91,91,246,0.18), rgba(34,195,209,0.1))',
              },
              '& .MuiListItemIcon-root': {
                color: t.palette.primary.main,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 3,
                height: '60%',
                borderRadius: 2,
                background: isDark
                  ? 'linear-gradient(180deg, #8b8bff, #5ee7f1)'
                  : 'linear-gradient(180deg, #5b5bf6, #22c3d1)',
              },
            },
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: { minWidth: 40, color: isDark ? 'rgba(236,238,252,0.7)' : 'rgba(11,16,32,0.6)' },
        },
      },
      MuiCircularProgress: {
        defaultProps: { size: 28 },
        styleOverrides: {
          root: { color: isDark ? '#8b8bff' : '#5b5bf6' },
        },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            border: `1px solid ${hairBorder}`,
          },
        },
      },
    },
  });

  return theme;
}
